import { useState } from 'react';
import { AlertCircle, Check, Link2 } from 'react-feather';
import Modal from '../Modal';
import { API_BASE_URL } from '../../config/api';

/* The organisation's directory link: connect it, describe it, remove it.
 *
 * "Connect" is a full navigation rather than a fetch, for the same reason the
 * login button is (see Login.js): the browser has to follow Microsoft's
 * redirects carrying its own session cookies. The difference is that this flow
 * is authenticated, so the authorization URL cannot be reached by putting a
 * bare href on a link, the Authorization header only exists on an XHR. Hence
 * POST first, then assign window.location to the URL that comes back.
 *
 * Unlinking is deliberately behind a typed confirmation. It cascades every
 * identity away, and the backend separately refuses when it would strand an
 * organisation with no password-holding admin; that 409 is worth reading
 * rather than swallowing, so its message is shown verbatim.
 */

const DirectoryCard = ({ tenant, canManage, authHeaders, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');

  const connect = async () => {
    if (busy) return;
    setBusy(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE_URL}/api/sso/start/link`, {
        method: 'POST',
        headers: authHeaders()
      });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(
          data?.detail ||
            data?.error?.message ||
            'Could not start the Microsoft connection.'
        );
      }
      // Leaves the SPA entirely; no state cleanup is worth doing after this.
      window.location.href = data.authorization_url;
    } catch (err) {
      setError(err.message || 'Could not start the Microsoft connection.');
      setBusy(false);
    }
  };

  const unlink = async () => {
    if (busy) return;
    setBusy(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE_URL}/api/sso/tenant`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(
          data?.detail ||
            data?.error?.message ||
            'Could not disconnect the directory.'
        );
      }
      setConfirming(false);
      setTyped('');
      await onChanged();
    } catch (err) {
      setError(err.message || 'Could not disconnect the directory.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="account-section-card account-card-compact">
      <div className="account-section-header">
        <span className="account-section-icon" aria-hidden="true">
          <Link2 />
        </span>
        <div className="account-section-heading">
          <h2 className="account-section-title">Microsoft Entra ID</h2>
          <p className="account-section-description">
            Let your team sign in with their existing Microsoft work accounts.
          </p>
        </div>
      </div>

      <div className="account-section-body">
        {error && (
          <div className="account-banner error" role="alert">
            <AlertCircle aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <div className={`account-callout${tenant ? ' is-active' : ''}`}>
          <span className="account-callout-icon" aria-hidden="true">
            {tenant ? <Check /> : <Link2 />}
          </span>
          <div className="account-callout-copy">
            <h3 className="account-callout-title">
              {tenant ? 'Directory connected' : 'No directory connected'}
            </h3>
            <p className="account-callout-text">
              {tenant ? (
                <>
                  Signing in with Microsoft is available to everyone in{' '}
                  <strong>{tenant.display_name || 'your directory'}</strong>.
                  {tenant.linked_by ? ` Connected by ${tenant.linked_by}.` : ''}
                </>
              ) : canManage ? (
                'Connect your organization’s directory and your team can sign in with the Microsoft account they already use, no extra password to manage.'
              ) : (
                'Your organization has not connected a Microsoft directory. Only an admin can change this.'
              )}
            </p>
            {tenant && (
              <p className="sso-tenant-id">
                Tenant ID <code>{tenant.tenant_id}</code>
              </p>
            )}
          </div>
        </div>
      </div>

      {canManage && (
        <div className="account-section-footer">
          {tenant ? (
            <button
              type="button"
              className="secondary-button sso-danger-button"
              onClick={() => {
                setConfirming(true);
                setTyped('');
                setError('');
              }}
              disabled={busy}
            >
              Disconnect directory
            </button>
          ) : (
            <button
              type="button"
              className="primary-button"
              onClick={connect}
              disabled={busy}
            >
              {busy && <span className="account-spinner" aria-hidden="true" />}
              {busy ? 'Redirecting…' : 'Connect Microsoft Entra ID'}
            </button>
          )}
        </div>
      )}

      <Modal
        isOpen={confirming}
        onClose={() => {
          if (!busy) {
            setConfirming(false);
            setTyped('');
          }
        }}
        title="Disconnect Microsoft Entra ID"
      >
        <p className="account-modal-text">
          Everyone in your organization who signs in with Microsoft will lose
          that route immediately, and the link between their Microsoft account
          and their profile here is deleted. Anyone without a password will need
          one before they can get back in.
        </p>

        {error && (
          <div className="account-banner error" role="alert">
            <AlertCircle aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <div className="account-field">
          <label htmlFor="sso-unlink-confirm">
            Type <strong>DISCONNECT</strong> to confirm
          </label>
          <input
            id="sso-unlink-confirm"
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </div>

        <div className="account-modal-actions">
          <button
            type="button"
            className="secondary-button"
            onClick={() => {
              setConfirming(false);
              setTyped('');
            }}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="primary-button sso-danger-button"
            onClick={unlink}
            disabled={busy || typed !== 'DISCONNECT'}
          >
            {busy && <span className="account-spinner" aria-hidden="true" />}
            {busy ? 'Disconnecting…' : 'Disconnect'}
          </button>
        </div>
      </Modal>
    </section>
  );
};

export default DirectoryCard;
